/* eslint-disable no-console */
/* eslint-disable max-len */
import { toast } from 'react-toastify';
import {
	updatePodcast as updatePodcastAPI,
	updateSeason,
	updateEpisode
} from '../../../../../../../../actions/podcast_management/podcast_management';

export const usePodcastOperations = (
	companyId?: string,
	contextHelpers?: {
		setLoading?: (loading: boolean) => void;
		setSelectedItems?: (items: any[]) => void;
		loadPodcasts?: () => void;
		viewMode?: string;
		currentPodcastId?: string | null;
		currentSeasonId?: string | null;
		loadSeasons?: (podcastId: string) => Promise<void>;
		loadEpisodes?: (podcastId: string, seasonId: string, offset?: number) => Promise<{ hasMore: boolean; totalLoaded: number }>;
	}
) =>
{
	// Validation helper functions (moved from context)
	const filterUpdateFields = (data: any): any =>
	{
		const filtered: any = {};

		// Process all fields in the data object
		Object.keys(data).forEach((key) =>
		{
			if (data[key] !== undefined && data[key] !== null && data[key] !== '')
			{
				filtered[key] = data[key];
			}
		});

		// Handle nested fields specially
		if (data.coverImage?.url)
		{
			filtered.coverImage = data.coverImage;
		}

		return filtered;
	};

	const updatePodcastSilent = async (podcastId: string, options: any): Promise<any> =>
	{
		try
		{
			// Filter and validate update options
			const updateOptions = filterUpdateFields(options);

			// Only proceed if we have valid options
			if (Object.keys(updateOptions).length === 0)
			{
				console.warn(`No valid update fields for podcast ${podcastId}`);

				return null;
			}

			// Call the API without toast notifications
			return await updatePodcastAPI(podcastId, updateOptions, false);
		}
		catch (error)
		{
			console.error(`Error updating podcast ${podcastId}:`, error);
			throw error;
		}
	};

	/**
	 * Handles updating multiple podcasts at once with the same data (unified handler)
	 * 
	 * @param companyId - The company ID
	 * @param podcasts - Array of podcast objects or IDs to update
	 * @param updatedData - The data to apply to all podcasts
	 * @param options - Optional settings for UI behavior
	 * @returns Promise with array of responses
	 */
	const handleMultiplePodcastUpdates = async (
		companyIdParam: string,
		podcasts: any[],
		updatedData: any,
		options?: {
			showToasts?: boolean;
		}
	): Promise<any> =>
	{
		if (!podcasts || podcasts.length === 0)
		{
			throw new Error('No podcast IDs provided for update');
		}

		const effectiveCompanyId = companyIdParam || companyId;
		const showToasts = options?.showToasts !== false; // Default to true

		try
		{
			// Set loading state if available
			if (contextHelpers?.setLoading)
			{
				contextHelpers.setLoading(true);
			}

			const isBatchUpdate = podcasts.length > 1;
			let toastId: any;

			if (showToasts)
			{
				toastId = toast.info(
					isBatchUpdate
						? `Updating ${podcasts.length} podcasts...`
						: 'Updating podcast...',
					{ autoClose: false }
				);
			}

			const updatePromises = podcasts.map((podcastItem) =>
			{
				const isStringId = typeof podcastItem === 'string';
				const existingData: any = isStringId ? {} : podcastItem;
				const podcastId = isStringId ? podcastItem : existingData.id;

				// Handle categories merging (like Video module)
				const mergedCategories = updatedData?.categoryIds !== undefined ? updatedData.categoryIds : undefined;

				// Handle tags merging (like Image module)
				const mergedTags = isBatchUpdate
					? Array.from(new Set([
						...(existingData?.tags || []),
						...(updatedData.tags || [])
					]))
					: updatedData.tags;

				// Create merged data object (following Image module pattern)
				const mergedData: any = {
					title                : updatedData.title ?? (isBatchUpdate ? existingData.title : undefined),
					description          : updatedData.description ?? (isBatchUpdate ? existingData.description : undefined),
					categoryIds          : mergedCategories,
					tags                 : mergedTags,
					status               : updatedData.status ?? (isBatchUpdate ? existingData.status : undefined),
					visibility           : updatedData.visibility ?? (isBatchUpdate ? existingData.visibility : undefined),
					allowDownload        : updatedData.allowDownload ?? (isBatchUpdate ? existingData.allowDownload : undefined),
					allowShare           : updatedData.allowShare ?? (isBatchUpdate ? existingData.allowShare : undefined),
					accessControl        : updatedData.accessControl ?? (isBatchUpdate ? existingData.accessControl : undefined),
					scheduledPublishDate : updatedData.scheduledPublishDate ?? (isBatchUpdate ? existingData.scheduledPublishDate : undefined)
				};

				// Add coverImage if provided
				if (updatedData.coverImage?.url || (isBatchUpdate && existingData.coverImage?.url))
				{
					mergedData.coverImage = updatedData.coverImage ?? (isBatchUpdate ? existingData.coverImage : undefined);
				}

				// Remove undefined fields (like Image module)
				Object.keys(mergedData).forEach((key) =>
				{
					if (mergedData[key] === undefined || mergedData[key] === null || mergedData[key] === '')
					{
						delete mergedData[key];
					}
				});

				return updatePodcastSilent(podcastId, mergedData);
			});

			const responses = await Promise.all(updatePromises);

			// Count successful updates (like Image module)
			const successCount = responses.filter((response) => response !== null).length;
			const failureCount = podcasts.length - successCount;

			// Update toast with results
			if (showToasts && toastId)
			{
				if (successCount > 0)
				{
					toast.update(toastId, {
						render : isBatchUpdate
							? `${successCount} podcast(s) updated successfully!${failureCount > 0 ? ` (${failureCount} failed)` : ''}`
							: 'Podcast updated successfully!',
						type      : toast.TYPE.SUCCESS,
						autoClose : 3000
					});

					// Refresh the current view if context helpers available
					if (contextHelpers?.viewMode === 'podcasts' && contextHelpers?.loadPodcasts)
					{
						contextHelpers.loadPodcasts();
					}
				}
				else
				{
					toast.update(toastId, {
						render : isBatchUpdate
							? `Failed to update all ${podcasts.length} podcasts`
							: 'Failed to update podcast',
						type      : toast.TYPE.ERROR,
						autoClose : 5000
					});
				}
			}

			const refreshEvent = new CustomEvent('contentStatusChanged', {
				detail : {
					contentId   : podcasts.map((podcast) => podcast.id),
					contentType : 'Podcasts',
					status      : 'updated'
				}
			});

			window.dispatchEvent(refreshEvent);

			return responses;
		}
		catch (error)
		{
			console.error('Error updating podcasts:', error);

			if (showToasts)
			{
				const errorMessage = podcasts.length > 1
					? `Failed to update some or all of the ${podcasts.length} podcasts`
					: 'Failed to update podcast';

				toast.error(errorMessage);
			}
			throw error;
		}
		finally
		{
			if (contextHelpers?.setLoading)
			{
				contextHelpers.setLoading(false);
			}
		}
	};

	/**
	 * Update a podcast or multiple podcasts (unified entry point)
	 * @param updatedData - Podcast data to update
	 * @param podcastIds - Array of podcast IDs to update (can be single or multiple)
	 * @returns Promise with updated podcast data
	 */
	const updatePodcast = async (updatedData: any, podcastIds: string[]) =>
	{
		try
		{
			// Validate input
			// Define all possible update keys (similar to Image module)
			const possibleKeys = [
				'title', 'description', 'visibility', 'categoryIds',
				'tags', 'allowDownload', 'allowShare', 'accessControl',
				'status', 'scheduledPublishDate'
			];

			// Build update options by filtering out undefined/null values
			const updateOptions = possibleKeys.reduce((options, key) =>
			{
				// Only add the key if it has a defined value
				if (updatedData[key] !== undefined && updatedData[key] !== null && updatedData[key] !== '')
				{
					options[key] = updatedData[key];
				}

				return options;
			}, {} as any);

			// Handle coverImage specially due to its nested structure
			if (updatedData.coverImage?.url)
			{
				updateOptions.coverImage = updatedData.coverImage;
			}
			else if (updatedData.coverImage)
			{
				updateOptions.coverImage = updatedData.coverImage;
			}

			// Only proceed with API call if we have valid options
			if (Object.keys(updateOptions).length > 0)
			{
				if (contextHelpers?.setLoading)
				{
					contextHelpers.setLoading(true);
				}

				if (contextHelpers?.viewMode === 'seasons')
				{
					// Handle season update
					if (contextHelpers.currentPodcastId && podcastIds[0])
					{
						const response = await updateSeason(contextHelpers.currentPodcastId, podcastIds[0], updateOptions, false);

						toast.success('Season updated successfully');

						// Refresh seasons data after successful update
						if (contextHelpers.loadSeasons)
						{
							await contextHelpers.loadSeasons(contextHelpers.currentPodcastId);
						}

						return response;
					}
					else
					{
						console.warn('Missing required IDs for season update');
						toast.error('Cannot update season: Missing required information');
					}
				}
				else if (contextHelpers?.viewMode === 'episodes')
				{
					// Handle episode update
					if (contextHelpers.currentPodcastId && contextHelpers.currentSeasonId && podcastIds[0])
					{
						const response = await updateEpisode(contextHelpers.currentPodcastId, podcastIds[0], updateOptions, false);

						toast.success('Episode updated successfully');

						// Refresh episodes data after successful update
						if (contextHelpers.loadEpisodes)
						{
							await contextHelpers.loadEpisodes(contextHelpers.currentPodcastId, contextHelpers.currentSeasonId);
						}

						return response;
					}
					else
					{
						console.warn('Missing required IDs for episode update');
						toast.error('Cannot update episode: Missing required information');
					}
				}
				else
				{
					// Handle podcast update (existing behavior)
					await handleMultiplePodcastUpdates(companyId!, podcastIds, updateOptions, { showToasts: true });
				}

				// Clear selection after successful update
				if (contextHelpers?.setSelectedItems)
				{
					contextHelpers.setSelectedItems([]);
				}
			}
			else
			{
				console.warn('No valid update fields provided');
				toast.warning('No changes to update');
			}
		}
		catch (error)
		{
			console.error('Error updating:', error);
			throw error;
		}
		finally
		{
			if (contextHelpers?.setLoading)
			{
				contextHelpers.setLoading(false);
			}
		}
	};

	return {
		updatePodcast,
		handleMultiplePodcastUpdates,
		updatePodcastSilent,
		filterUpdateFields
	};
};